import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, CurrencyCircleDollar, Copy, ArrowLeft, Check } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TierIcon } from '@/components/ui/tier-icon';
import { useAuth } from '@/contexts/AuthContext';
import { billingApi, type CryptoPaymentRequest } from '@/api/client';
import { UPGRADE_EVENT } from '@/lib/upgrade';
import { requiredTier, tierRank, tierNameKey, type Feature, type Tier } from '@/lib/tiers';
import { fmtUsd, fmtNumber, format } from '@/lib/format';
import { cn } from '@/lib/utils';

const PLANS: Tier[] = ['telegram', 'regular', 'premium'];

/****************
 * THE PLANS POP-UP. Guillaume, 2026-09-26: "when people try to access things
 * that are unavailable to them, a pop-up so they can pay and chose a
 * membership subscription should appear and allow them to subscribe ... built
 * right now so when we have our stripe account, or any other service provider
 * we can immediately connect it", plus paying in crypto "directly from their
 * broker to our wallet".
 *
 * Opened by any lock (lib/upgrade.ts). Two steps: pick a plan (the cheapest
 * one that opens what was touched is marked), then pay by card (the provider's
 * page) or crypto (our address + an amount unique to this request). Each way
 * says plainly when it is not open yet; the server decides, from its keys
 * (backend billing.go), so connecting Stripe or a wallet needs no change here.
 ****************/
export function UpgradeDialog() {
  const { t } = useTranslation('common');
  const { tier: myTier } = useAuth();
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState<Feature | undefined>();
  const [plan, setPlan] = useState<Tier | null>(null);
  const [network, setNetwork] = useState('');
  const [busy, setBusy] = useState<'card' | 'crypto' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [crypto, setCrypto] = useState<CryptoPaymentRequest | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const f = (e as CustomEvent<{ feature?: Feature }>).detail?.feature;
      setFeature(f);
      setPlan(null);
      setCrypto(null);
      setError(null);
      setOpen(true);
    };
    window.addEventListener(UPGRADE_EVENT, onOpen);
    return () => window.removeEventListener(UPGRADE_EVENT, onOpen);
  }, []);

  const options = useQuery({
    queryKey: ['billing', 'options'],
    queryFn: () => billingApi.options(),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const opts = options.data;
  useEffect(() => {
    if (opts?.crypto.networks.length && !network) setNetwork(opts.crypto.networks[0]);
  }, [opts, network]);

  const needed = feature ? requiredTier(feature) : null;
  const price = (p: Tier) => opts?.prices_usd_month?.[p] ?? null;

  const explain = (e: unknown) => {
    const code = e instanceof Error ? e.message : '';
    if (code.includes('not_configured')) return t('upgrade.error.notOpen');
    if (code.includes('price_not_set')) return t('upgrade.error.noPrice');
    return t('upgrade.error.failed');
  };

  const payByCard = async () => {
    if (!plan) return;
    setBusy('card'); setError(null);
    try {
      const { url } = await billingApi.checkout(plan);
      window.location.assign(url);
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(null);
    }
  };

  const payByCrypto = async () => {
    if (!plan || !network) return;
    setBusy('crypto'); setError(null);
    try {
      setCrypto(await billingApi.crypto(plan, network));
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(null);
    }
  };

  const copy = (key: string, text: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {plan ? t('upgrade.payTitle', { plan: t(tierNameKey(plan)) }) : t('upgrade.title')}
          </DialogTitle>
          <DialogDescription>
            {plan
              ? t('upgrade.paySubtitle')
              : needed
                ? t('upgrade.subtitleFeature', { tier: t(tierNameKey(needed)) })
                : t('upgrade.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {!plan ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {PLANS.map((p) => {
              const tooLow = needed !== null && tierRank(p) < tierRank(needed);
              const current = myTier === p;
              const pick = needed === p;
              const pr = price(p);
              return (
                <button
                  key={p}
                  type="button"
                  disabled={tooLow || current}
                  onClick={() => { setPlan(p); setError(null); }}
                  className={cn(
                    'relative flex flex-col items-start gap-2 rounded-xl border p-4 text-start transition-colors',
                    pick ? 'border-primary bg-primary/[0.06]' : 'border-border/60 hover:bg-white/[0.03]',
                    (tooLow || current) && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                  )}
                >
                  {pick && (
                    <span className="absolute -top-2 end-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                      {t('upgrade.unlocksThis')}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <TierIcon tier={p} size={18} />
                    <span className="font-semibold">{t(tierNameKey(p))}</span>
                  </div>
                  <p className="text-lg font-bold">
                    {pr !== null ? t('upgrade.perMonth', { price: fmtUsd(pr, pr % 1 ? 2 : 0) }) : (
                      <span className="text-sm font-medium text-muted-foreground">{t('upgrade.priceSoon')}</span>
                    )}
                  </p>
                  <p className="text-[12px] text-muted-foreground leading-snug">{t(`tier.summary.${p}`)}</p>
                  {current && <span className="text-[11px] text-muted-foreground">{t('upgrade.yourPlan')}</span>}
                  {tooLow && <span className="text-[11px] text-muted-foreground">{t('upgrade.notIncluded')}</span>}
                </button>
              );
            })}
          </div>
        ) : crypto ? (
          /* ── Crypto: where to send, how much ─────────────────────────── */
          <div className="space-y-3 text-sm">
            <p>{t('upgrade.crypto.instructions', { amount: fmtNumber(crypto.amount_usdt, 2, 2), network: crypto.network })}</p>
            {[
              { k: 'address', label: t('upgrade.crypto.address'), v: crypto.address, mono: true },
              { k: 'amount', label: t('upgrade.crypto.amount'), v: `${fmtNumber(crypto.amount_usdt, 2, 2)} USDT`, copyText: crypto.amount_usdt.toFixed(2) },
              { k: 'network', label: t('upgrade.crypto.network'), v: crypto.network },
              { k: 'ref', label: t('upgrade.crypto.reference'), v: crypto.reference },
            ].map((row) => (
              <div key={row.k} className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2">
                <span className="w-24 shrink-0 text-[12px] text-muted-foreground">{row.label}</span>
                <span className={cn('flex-1 min-w-0 break-all', row.mono && 'font-mono text-[12px]')}>{row.v}</span>
                <button type="button" onClick={() => copy(row.k, row.copyText ?? row.v)} className="shrink-0 text-muted-foreground hover:text-foreground" aria-label={t('upgrade.crypto.copy')}>
                  {copied === row.k ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            ))}
            <p className="text-[12px] text-warning">{t('upgrade.crypto.exactWarning')}</p>
            <p className="text-[12px] text-muted-foreground">
              {t('upgrade.crypto.after', { time: format(new Date(crypto.expires_at), 'MMM d, HH:mm') })}
            </p>
          </div>
        ) : (
          /* ── How to pay ──────────────────────────────────────────────── */
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-border/60 p-4 space-y-2">
              <div className="flex items-center gap-2 font-semibold"><CreditCard size={18} />{t('upgrade.card.title')}</div>
              <p className="text-[12px] text-muted-foreground">{t('upgrade.card.body')}</p>
              {opts?.card.available ? (
                <Button className="w-full" disabled={busy !== null} onClick={() => void payByCard()}>
                  {busy === 'card' ? t('upgrade.working') : t('upgrade.card.button')}
                </Button>
              ) : (
                <p className="text-[12px] font-medium text-warning">{t('upgrade.soon')}</p>
              )}
            </div>
            <div className="rounded-xl border border-border/60 p-4 space-y-2">
              <div className="flex items-center gap-2 font-semibold"><CurrencyCircleDollar size={18} />{t('upgrade.crypto.title')}</div>
              <p className="text-[12px] text-muted-foreground">{t('upgrade.crypto.body')}</p>
              {opts?.crypto.available ? (
                <>
                  <select
                    value={network}
                    onChange={(e) => setNetwork(e.target.value)}
                    className="w-full rounded-md border border-border/60 bg-background px-2 py-1.5 text-sm"
                    aria-label={t('upgrade.crypto.network')}
                  >
                    {opts.crypto.networks.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <Button className="w-full" variant="outline" disabled={busy !== null || !network} onClick={() => void payByCrypto()}>
                    {busy === 'crypto' ? t('upgrade.working') : t('upgrade.crypto.button')}
                  </Button>
                </>
              ) : (
                <p className="text-[12px] font-medium text-warning">{t('upgrade.soon')}</p>
              )}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        {plan && (
          <button
            type="button"
            onClick={() => { setPlan(null); setCrypto(null); setError(null); }}
            className="inline-flex items-center gap-1 self-start text-[12px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={12} className="rtl:-scale-x-100" />
            {t('upgrade.back')}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
